/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable id-length */
import React, { useMemo, useEffect } from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useDesignStore } from '../../store/design/context';
import {
  useConfiguratorStore,
  useMediaStore,
  useProductInfoStore,
  useProductDataStore,
  useDigitalDataStore,
  useDropZoneStore,
} from '../../store';
import { Draggable } from '../Draggable';
import { Droppable } from '../Droppable';
import DropzoneArrowIcon from './components/DropzoneArrow';
import { calculate3DDropZones, getProductTypeFromMetadata, parseGridSpan } from './utils/index';
import { groupProductsByType } from './utils/grouping/groupProductsByType';
import { getBundleSkusAndPrice } from '../../utils/bundle-utils';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../constants';
import { computeDraggabilityMap } from './utils/draggability/index';
import { useClickAway } from '../../hooks/useClickAway';

const StackInner = () => {
  const matches = useBreakpoint('md');
  const isDesktop = matches.greater;
  const design = useDesignStore((state) => state.design);
  const stackLayoutDirection = useProductInfoStore(
    (state) => state.productInfo?.stackLayoutDirection || 'right'
  );
  const metadata = useConfiguratorStore((state) => state.configurator.metadata);
  const media = useMediaStore((state) => state.assets);
  const selectVisualizerItem = useDesignStore((state) => state.selectVisualizerItem);
  const addProduct = useDesignStore((state) => state.add);
  const addBundleProducts = useDesignStore((state) => state.addBundleProducts);
  const productData = useProductDataStore((state) => state.productData);
  const bundles = useDesignStore((state) => state.bundles);
  const trackAddItemToStackEvent = useDigitalDataStore((state) => state.trackAddItemToStackEvent);
  const isInitial = design.products.length < 1;
  const selectedVisualizerItemStackId = design?.selectedVisualizerItemStackId;
  const dropZones = useDropZoneStore((state) => state.dropZones);
  const addDropZones = useDropZoneStore((state) => state.addDropZones);
  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);
  const resetSelected = useDesignStore((state) => state.resetSelected);

  const selectedProductType = design.selectedProduct
    ? getProductTypeFromMetadata(metadata?.[design.selectedProduct.choiceId]?.metadata)
    : null;

  useEffect(() => {
    const zones = selectedProductType
      ? calculate3DDropZones(selectedProductType, design.products, metadata, isInitial)
      : [];
    addDropZones(zones);
  }, [design.selectedProduct]);

  const productGroupsByType = groupProductsByType(design.products, metadata);

  const addProductToDesign = (choiceId, choiceMetadata, zone, tubeSpan) => {
    const {
      BundleDesign: bundleDesign = null,
      Sku: sku = null
    } = choiceMetadata;

    if (bundleDesign) {
      addBundleProducts(metadata, choiceId, bundleDesign, zone);
      const skus = bundles?.data?.[bundleDesign]?.bundleSkus;
      const bundleSkusAndPrices = getBundleSkusAndPrice(
        skus,
        productData
      );
      return trackAddItemToStackEvent(bundleSkusAndPrices);
    }

    addProduct(choiceId, choiceMetadata, [...zone, tubeSpan]);
    return trackAddItemToStackEvent(sku, productData?.[sku]?.pricing?.value);
  };

  const logChoiceAddition = async (choiceId, choiceMetadata) => {
    logNewRelicCustom(NEW_RELIC_EVENTS.CLICK_ADD_CHOICE, {
      choiceId,
      ...choiceMetadata
    });
  };

  const handlePlace3D = (x, y, z, id) => {
    if (!design.selectedProduct) return;

    const selectedProductChoiceId = design.selectedProduct.choiceId;
    const choiceMetadata = metadata?.[selectedProductChoiceId]?.metadata;
    const { x: w, y: d, height } = parseGridSpan(choiceMetadata?.GridSpan);

    // Add to design.products
    addProductToDesign(selectedProductChoiceId, choiceMetadata, [y, x], height);
    logChoiceAddition(selectedProductChoiceId, choiceMetadata);
    resetDropZones();
  };
  const handleClick = (stackItemId, isDraggable) => {
    if (isDraggable && stackItemId) {
      selectVisualizerItem(stackItemId, resetDropZones);
    }
  };
  const draggabilityMap = useMemo(
    () => computeDraggabilityMap(
      design,
      productGroupsByType,
      metadata,
      { alwaysLockBaseFull: false }
    ),
    [design]
  );
  const renderProduct = (product, index, isDraggableOverride) => {
    const productMetadata = metadata?.[product.choiceId]?.metadata;
    const height = productMetadata?.ProductStackHeight || 10;
    let width = Number(productMetadata?.ProductBaseWidth) || (product.type === 'wide' ? 200 : 125);
    let right = Number(productMetadata?.Right);
    let bottom = Number(productMetadata?.Bottom);
    let imagePosition = {};
    // CODE SMELL: Hardcoded position adjustments for specific choices
    if (product.choiceId === 263403) {
      width = 135;
      right = -5;
      bottom = -8;
    }
    if (product.choiceId === 257615) { // ridgid organizer
      // width = 130;
      imagePosition = { width: 140 };
    }
    if (product.choiceId === 257605) { // ridgid toolbox
      right = -20;
    }

    if (product.choiceId === 128431) {
      bottom = 45;
    }

    if (product.choiceId === 257606) { // ridgid pro gear cart attachment
      right = -26;
    }

    const isDraggable = isDraggableOverride !== undefined
      ? isDraggableOverride
      : true;
    return (
      <div
        style={{
          border: 'none',
          height: Number(height),
          width: width || 200,
          zIndex: index + 1,
          right: right || 'auto',
          bottom: bottom || 'auto',
          ...(selectedVisualizerItemStackId === product.stackItemId && {
            filter: 'drop-shadow(0 0 0.75rem lime)',
            backdropFilter: 'blur(0)',
            transform: 'translate3d(0, 0, 0)',
          })
        }}
        className="stack-item"
        data-sku={product?.Sku}
        data-choice-id={product?.choiceId}
        role="button"
        onClick={() => handleClick(product.stackItemId, isDraggable)}
        tabIndex={0}
      >
        <Draggable
          key={product.stackItemId}
          id={product.stackItemId}
          isVisualizerDrag
          isDraggable={isDraggable}
          choiceId={product.choiceId}
          bundleId={product?.choiceBundle?.id}
          imagePosition={imagePosition}
        >
          <img
            style={{ border: 'none' }}
            alt={`single-stack-${product.stackItemId}`}
            src={media?.[product.choiceId]?.mediaUrl}
            className="stack-item-image"
            height="auto"
            width="auto"
          />
        </Draggable>
      </div>

    );
  };

  const renderStacksInOrder = (group, section) => {
    const stackGroups = group[section].stackGroups || [];
    return stackGroups.map((stackGroup, groupIndex) => {
      if (stackGroup.type === 'multi') {
      // Filter products for this specific group
        const groupProducts = stackGroup.products;
        const leftProducts = groupProducts.filter((p) => p.Origin[1] < 2);
        const rightProducts = groupProducts.filter((p) => p.Origin[1] >= 2);
        return (
          <div className="multi-stack" style={{ zIndex: groupIndex + 1 }} key={`${section}-multi-${groupIndex}`}>
            <div className="left-stack">
              {leftProducts.length > 0 && (
                leftProducts.map(
                  (product, i) => renderProduct(product, i, draggabilityMap[product.stackItemId])).reverse()
              )}
            </div>
            <div className="right-stack">
              {rightProducts.length > 0 && (
                rightProducts.map(
                  (product, i) => renderProduct(product, i, draggabilityMap[product.stackItemId])).reverse()
              )}
            </div>
          </div>
        );
      } if (stackGroup.type === 'single') {
        return (
          <div className="single-stack" style={{ zIndex: groupIndex + 1 }} key={`${section}-single-${groupIndex}`}>
            {stackGroup.products.map(
              (product, i) => renderProduct(product, i, draggabilityMap[product.stackItemId])
            ).reverse()}
          </div>
        );
      }
      return null;
    });
  };

  const ref = useClickAway(() => {
    resetSelected();
    resetDropZones();
  });

  let multiStackLeft = '7%';
  const shouldMoveLeft = stackLayoutDirection === 'left';
  if (!isDesktop) {
    multiStackLeft = shouldMoveLeft ? '-14%' : '14%';
  } else if (shouldMoveLeft) {
    multiStackLeft = '-7%';
  }

  return (
    <div className="stack-inner-wrapper">
      <div className="stack-inner">
        <div
          className="stack-container"
          style={productGroupsByType[0]?.type === 'multi-stack' ? { left: multiStackLeft } : {}}
          ref={ref}
        >
          {/* Render 3D Drop Zones */}
          <div className="dropzone-box">
            <div className="sui-align-center">
              <div className="sui-h-full sui-z-[99] sui-relative sui-flex sui-flex-col">
                {dropZones.map((zone, index) => {
                  const dropzoneType = getProductTypeFromMetadata(
                    metadata?.[design.selectedProduct?.choiceId]?.metadata
                  );

                  return zone.isBlocked ? (
                    <div
                      key={zone.id}
                      className={
                        `child-dropzone-blocked${dropzoneType === 'wide' ? ' child-dropzone-blocked-wide' : ''}`
                      }
                    />
                  ) : (
                    <>
                      <DropzoneArrowIcon type={zone.id} />
                      <div
                        key={zone.id}
                        className={`parent-${zone.id} dropzone-box-target-${zone.id} dropzone-box-target-visible`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePlace3D(zone.x, zone.y, zone.z, zone.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePlace3D(zone.x, zone.y, zone.z, zone.id);
                          }
                        }}
                        title="Click to place product here"
                        type="button"
                        aria-label={`Drop zone for ${dropzoneType} product at position ${zone.x}, ${zone.y}, ${zone.z}`}
                      >
                        <Droppable id={zone.id} key={`${zone.id}-${index}`} origin={[zone.y, zone.x]}>
                          <div
                            role="button"
                            aria-label={`child-${zone.id}`}
                            tabIndex={0}
                            className={`child-${zone.id} dropzone-global`}
                          />
                        </Droppable>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>
          </div>
          <>
            {productGroupsByType.map((group, index) => {
              if (group.type === 'multi-stack') {
                return (
                  <div className="default-container" style={{ zIndex: index }} key={`multi-stack-${index}`}>
                    <div key={`multi-stack-${index}`} className="multi-stack-container">
                      {/* Front Section with dynamic ordering */}
                      <div name="Front" className="front">
                        {renderStacksInOrder(group, 'front').reverse()}
                      </div>

                      {/* Rear Section with dynamic ordering */}
                      <div name="Rear" className="rear">
                        {renderStacksInOrder(group, 'rear').reverse()}
                      </div>
                    </div>
                  </div>
                );
              } if (group.type === 'full') {
                return (
                  <div key={`full-${index}`} className="sui-relative" style={{ zIndex: index }}>
                    {renderProduct(group.product, 0, draggabilityMap[group.product.stackItemId])}
                  </div>
                );
              }
              return null;
            }).reverse()}
          </>
        </div>
      </div>
    </div>
  );
};

export default StackInner;
